import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
const easeOut = [0.16, 1, 0.3, 1] as const;
export function HeroSection() {
  const {
    lang
  } = useLanguage();
  const trust = [{
    value: '100+',
    label: lang === 'es' ? 'Negocios construidos' : 'Businesses built'
  }, {
    value: '100%',
    label: lang === 'es' ? 'Estrategia a medida' : 'Tailored Strategy'
  }, {
    value: '<2 sem',
    valueEn: '<2 wks',
    label: lang === 'es' ? 'Entrega promedio' : 'Average Delivery'
  }];
  const fadeUp = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.6,
      ease: easeOut,
      delay
    }
  });
  const fade = (delay: number, duration = 0.6) => ({
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    transition: {
      duration,
      ease: easeOut,
      delay
    }
  });
  return <section id="inicio" className="relative bg-[#FAF8F5] w-full lg:min-h-[100vh] overflow-hidden" data-id="element-212">
      <div className="grid grid-cols-1 lg:grid-cols-[52%_48%] lg:min-h-[100vh]" data-id="element-213">
        {/* ───────────────────────── PHOTO COLUMN ───────────────────────── */}
        <motion.div {...fade(0, 1.2)} className="relative order-1 lg:order-2 w-full h-[56vw] lg:h-[100vh] overflow-hidden rounded-b-[28px] lg:rounded-none" data-id="element-214">
          <img src="https://cdn.magicpatterns.com/uploads/4hmVMs8fGXLBDG4G7Yo7Co/annysol-hero.jpg.png" alt="Annysol Navarro, fundadora de Inkova Creation" loading="eager" className="w-full h-full object-cover [object-position:top_center]" data-id="element-215" />

          {/* Film grain — very subtle cinematic texture */}
          <div className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-[0.025] z-[2]" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='grain'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23grain)'/%3E%3C/svg%3E\")"
        }} data-id="element-216" />

          {/* Left blend gradient — photo melts into cream (desktop) */}
          <div className="pointer-events-none absolute top-0 left-0 h-full w-[25%] z-[2] hidden lg:block" style={{
          background: 'linear-gradient(to right, #FAF8F5 0%, rgba(250,248,245,0.6) 50%, transparent 100%)'
        }} data-id="element-217" />

          {/* Bottom vignette (desktop) */}
          <div className="pointer-events-none absolute bottom-0 left-0 w-full h-[30%] z-[2] hidden lg:block" style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(15,10,30,0.12) 100%)'
        }} data-id="element-218" />

          {/* Bottom fade into cream (mobile) */}
          <div className="pointer-events-none absolute bottom-0 left-0 w-full h-full z-[2] lg:hidden" style={{
          background: 'linear-gradient(to bottom, transparent 40%, #FAF8F5 100%)'
        }} data-id="element-219" />

          {/* Year stamp — editorial detail */}
          <span className="absolute bottom-10 right-8 z-[3] hidden lg:block font-sans text-[11px] tracking-[3px] text-white/50" data-id="element-220">
            EST. 2018 · MIAMI
          </span>
        </motion.div>

        {/* ───────────────────────── TEXT COLUMN ───────────────────────── */}
        <div className="relative order-2 lg:order-1 z-[2] flex flex-col justify-center bg-[#FAF8F5] -mt-10 lg:mt-0 px-6 py-10 pb-16 lg:py-0 lg:pl-[7vw] lg:pr-12" data-id="element-221">
          {/* 1. Eyebrow label */}
          <motion.div {...fade(0.1)} className="mb-6" data-id="element-222">
            <span className="inline-block align-middle w-[32px] h-[2px] bg-[#6B3FA0] mr-3" data-id="element-223" />
            <span className="align-middle font-sans text-[11px] font-medium tracking-[3px] uppercase text-[#6B3FA0]" data-id="element-224">
              {lang === 'es' ? 'Miami · Agencia Digital' : 'Miami · Digital Agency'}
            </span>
          </motion.div>

          {/* 2. H1 */}
          <h1 className="font-display text-[34px] lg:text-[52px] font-bold mb-6" style={{
          lineHeight: 1.15
        }} data-id="element-225">
            {lang === 'es' ? <>
                <motion.span {...fadeUp(0.2)} className="block text-[#0F0A1E] tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-226">
                  Tu negocio digital,
                </motion.span>
                <motion.span {...fadeUp(0.35)} className="block text-[#0F0A1E] tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-227">
                  construido desde tu
                </motion.span>
                <motion.span {...fadeUp(0.5)} className="block italic font-bold text-[#6B3FA0] tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-228">
                  identidad y propósito.
                </motion.span>
              </> : <>
                <motion.span {...fadeUp(0.2)} className="block text-[#0F0A1E] tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-229">
                  Your digital business,
                </motion.span>
                <motion.span {...fadeUp(0.35)} className="block text-[#0F0A1E] tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-230">
                  built from your
                </motion.span>
                <motion.span {...fadeUp(0.5)} className="block italic font-bold text-[#6B3FA0] tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-231">
                  identity and purpose.
                </motion.span>
              </>}
          </h1>

          {/* 3. Subtitle */}
          <motion.p {...fade(0.6)} className="font-sans text-[15px] lg:text-[16px] text-[#5a5368] max-w-[440px] mb-10" style={{
          lineHeight: 1.8
        }} data-id="element-232">
            {lang === 'es' ? 'Diseñamos negocios digitales con claridad, estrategia e intención. Tu presencia digital debe comunicar tu valor antes de que hables.' : 'We design digital businesses with clarity, strategy and intention. Your digital presence should communicate your value before you speak.'}
          </motion.p>

          {/* 4. Divider line */}
          <motion.div {...fade(0.7)} className="w-[48px] h-[1px] mb-8" style={{
          background: 'rgba(107,63,160,0.3)'
        }} data-id="element-233" />

          {/* 5. CTA buttons */}
          <motion.div {...fade(0.75)} className="flex flex-col sm:flex-row sm:items-start gap-y-2 gap-x-4" data-id="element-234">
            <div className="flex flex-col items-stretch sm:items-start" data-id="element-235">
              <a href="https://calendly.com/inkovacreation" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-[#6B3FA0] text-white font-sans font-semibold text-[13px] tracking-[0.8px] uppercase px-8 py-[14px] rounded-[6px] transition-colors duration-300 hover:bg-[#5a3389]" data-id="element-236">
                {lang === 'es' ? 'Da el primer paso' : 'Take the first step'}
              </a>
              <span className="font-sans text-[11px] text-[#9b8aaa] mt-2.5 text-center sm:text-left" data-id="element-237">
                {lang === 'es' ? 'Sesión Diagnóstico · $50 · 30 min' : 'Diagnostic Session · $50 · 30 min'}
              </span>
            </div>
            <a href="https://wa.me/13054781618" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center border-[1.5px] border-[#6B3FA0] text-[#6B3FA0] bg-transparent font-sans font-semibold text-[13px] tracking-[0.8px] uppercase px-8 py-[14px] rounded-[6px] transition-colors duration-300 hover:bg-[#6B3FA0]/5" data-id="element-238">
              {lang === 'es' ? 'Tengo una pregunta' : 'I have a question'}
            </a>
          </motion.div>

          {/* 6. Trust indicators */}
          <motion.div {...fade(0.9)} className="grid grid-cols-3 gap-4 sm:flex sm:items-center sm:gap-10 mt-10 lg:mt-14" data-id="element-239">
            {trust.map((item, index) => <Fragment key={index} data-id="element-240">
                <div className="flex flex-col items-center text-center sm:items-start sm:text-left" data-id="element-241">
                  <span className="font-display font-bold text-[28px] lg:text-[36px] text-[#0F0A1E] leading-none" data-id="element-242">
                    {lang === 'en' && item.valueEn ? item.valueEn : item.value}
                  </span>
                  <span className="font-sans text-[11px] tracking-[1.5px] uppercase text-[#9b8aaa] mt-1" data-id="element-243">
                    {item.label}
                  </span>
                </div>
                {index < trust.length - 1 && <span className="hidden sm:block w-[1px] h-[40px]" style={{
              background: 'rgba(107,63,160,0.2)'
            }} data-id="element-244" />}
              </Fragment>)}
          </motion.div>
        </div>
      </div>
    </section>;
}